/**
 * Skill Matrix Page
 *
 * Visual grid showing operators (rows) × SOPs (columns) with
 * color-coded average scores. Click any cell for drill-down history.
 *
 * Phase 9.3: Supports groupBy (shift/position/station) view.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Grid3X3,
  Download,
  Filter,
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Users,
  Clock,
  Briefcase,
  Locate,
} from 'lucide-react';
import { useSkillMatrix, useSkillMatrixCell, useSkillMatrixGroupCell, useExportSkillMatrix } from '../../hooks/useSkillMatrix';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { GroupedMatrixView } from '../../components/skill-matrix/GroupedMatrixView';
import type { SkillMatrixGroupBy, SkillMatrixResponse, SkillMatrixGroupedResponse } from '@guild/shared';
import { getScoreColor } from '../../lib/score-colors';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { Tooltip } from '../../components/ui/Tooltip';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';

// Type guard to check if response is grouped
function isGroupedResponse(
  data: SkillMatrixResponse | SkillMatrixGroupedResponse | undefined
): data is SkillMatrixGroupedResponse {
  return data !== undefined && 'groupBy' in data && 'groups' in data;
}

// Type guard to check if response is flat (operator) matrix
function isFlatResponse(
  data: SkillMatrixResponse | SkillMatrixGroupedResponse | undefined
): data is SkillMatrixResponse {
  return data !== undefined && 'operators' in data && 'cells' in data;
}

export function SkillMatrixPage() {
  useDocumentTitle('Skill Matrix');
  // Filter state
  const [departmentId, setDepartmentId] = useState<string>('');
  // Phase 9.3: GroupBy state
  const [groupBy, setGroupBy] = useState<SkillMatrixGroupBy>('operator');

  // Cell drill-down state
  const [selectedCell, setSelectedCell] = useState<{ operatorId: string; sopId: string } | null>(null);

  // Group cell drill-down state
  const [selectedGroupCell, setSelectedGroupCell] = useState<{
    groupBy: string;
    groupId: string;
    sopId: string;
  } | null>(null);

  // Build filter object
  const filters = useMemo(() => {
    const f: {
      departmentId?: string;
      groupBy?: SkillMatrixGroupBy;
    } = {};
    if (departmentId) f.departmentId = departmentId;
    if (groupBy !== 'operator') f.groupBy = groupBy;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [departmentId, groupBy]);

  // Fetch data
  const { data: matrixData, isLoading, error } = useSkillMatrix(filters);
  const { data: departments } = useDepartmentLookup();
  const { data: cellData, isLoading: isCellLoading } = useSkillMatrixCell(
    selectedCell?.operatorId ?? null,
    selectedCell?.sopId ?? null
  );

  // Group cell drill-down data
  const { data: groupCellData, isLoading: isGroupCellLoading } = useSkillMatrixGroupCell(
    selectedGroupCell?.groupBy ?? null,
    selectedGroupCell?.groupId ?? null,
    selectedGroupCell?.sopId ?? null,
    departmentId || null
  );

  const exportMutation = useExportSkillMatrix();

  // Build cell lookup map (only for flat view)
  const cellMap = useMemo(() => {
    const map = new Map<string, NonNullable<SkillMatrixResponse>['cells'][number]>();
    if (isFlatResponse(matrixData)) {
      matrixData.cells?.forEach((cell) => {
        map.set(`${cell.operatorId}:${cell.sopId}`, cell);
      });
    }
    return map;
  }, [matrixData]);

  // Score color with hover for interactive cells
  const getScoreColorWithHover = (score: number | null) => {
    const base = getScoreColor(score, 'badge');
    if (score === null) return base;
    if (score >= 80) return `${base} hover:bg-emerald-200`;
    if (score >= 60) return `${base} hover:bg-amber-200`;
    return `${base} hover:bg-red-200`;
  };

  // Trend icon
  const TrendIcon = ({ trend }: { trend: string }) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp size={14} className="text-emerald-500" />;
      case 'declining':
        return <TrendingDown size={14} className="text-red-500" />;
      case 'stable':
        return <Minus size={14} className="text-gray-400" />;
      default:
        return <AlertCircle size={12} className="text-gray-300" />;
    }
  };

  const handleExport = () => {
    exportMutation.mutate(filters);
  };

  const handleCellClick = (operatorId: string, sopId: string) => {
    setSelectedCell({ operatorId, sopId });
  };

  const closeDrillDown = () => {
    setSelectedCell(null);
  };

  // Group cell drill-down handlers
  const handleGroupCellClick = (groupId: string, sopId: string) => {
    if (groupBy && groupBy !== 'operator') {
      setSelectedGroupCell({ groupBy, groupId, sopId });
    }
  };

  const closeGroupDrillDown = () => {
    setSelectedGroupCell(null);
  };

  // Drill from group modal into individual operator modal
  const handleOperatorDrillDown = (operatorId: string, sopId: string) => {
    setSelectedGroupCell(null); // close group modal
    setSelectedCell({ operatorId, sopId }); // open individual drill-down
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  // Get display label for groupBy option (station → Zone due to terminology swap)
  const getGroupByLabel = (value: SkillMatrixGroupBy) => {
    if (value === 'station') return 'Zone';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  // Get icon for groupBy option
  const getGroupByIcon = (value: SkillMatrixGroupBy) => {
    switch (value) {
      case 'operator':
        return <Users size={14} />;
      case 'shift':
        return <Clock size={14} />;
      case 'position':
        return <Briefcase size={14} />;
      case 'station':
        return <Locate size={14} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <Grid3X3 size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Skill Matrix
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            {groupBy === 'operator'
              ? 'Visual overview of operator proficiency across all SOPs'
              : `Performance overview grouped by ${getGroupByLabel(groupBy).toLowerCase()}`}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* GroupBy Selector (Phase 9.3) */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 hidden sm:inline">View by:</span>
            <div className="flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
              {(['operator', 'shift', 'position', 'station'] as SkillMatrixGroupBy[]).map((option) => (
                <button
                  key={option}
                  onClick={() => setGroupBy(option)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    groupBy === option
                      ? 'bg-guild-green text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {getGroupByIcon(option)}
                  <span>{getGroupByLabel(option)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Department Filter */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter size={16} />
          </div>
          <select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            className="appearance-none rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm text-gray-700 transition-colors hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20"
          >
            <option value="">All Departments</option>
            {departments?.map((dept) => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>

          {/* Export Button */}
          <button
            onClick={handleExport}
            disabled={exportMutation.isPending}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={16} />
            {exportMutation.isPending ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Legend (only for operator view) */}
      {groupBy === 'operator' && (
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <span className="text-sm font-medium text-gray-700">Score Legend:</span>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
              80-100
            </span>
            <span className="text-xs text-gray-500">Proficient</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
              60-79
            </span>
            <span className="text-xs text-gray-500">Developing</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
              0-59
            </span>
            <span className="text-xs text-gray-500">Needs Improvement</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-400">
              —
            </span>
            <span className="text-xs text-gray-500">No Data</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col items-center justify-center py-16">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-sm text-gray-500">Loading skill matrix...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-sm font-medium text-gray-900">Failed to load skill matrix</p>
          </div>
        </div>
      )}

      {/* Grouped View (Phase 9.3) */}
      {!isLoading && !error && isGroupedResponse(matrixData) && (
        <GroupedMatrixView data={matrixData} onCellClick={handleGroupCellClick} />
      )}

      {/* Flat Operator Matrix View */}
      {!isLoading && !error && isFlatResponse(matrixData) && (
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
          {!matrixData?.operators?.length || !matrixData?.sops?.length ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50">
                <Grid3X3 size={32} className="text-gray-400" />
              </div>
              <p className="mt-4 text-sm font-medium text-gray-900">No data available</p>
              <p className="mt-1 text-sm text-gray-500">
                Add operators and SOPs, then create assessments to populate the matrix.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="sticky left-0 z-10 min-w-[200px] border-b border-r border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Operator
                    </th>
                    {matrixData.sops.map((sop) => (
                      <th
                        key={sop.id}
                        className="min-w-[120px] border-b border-gray-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                      >
                        <Tooltip content={sop.title} side="bottom" className="w-full justify-center">
                          <div className="line-clamp-2 cursor-help">
                            {sop.title}
                          </div>
                        </Tooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixData.operators.map((operator, rowIdx) => (
                    <tr key={operator.id} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}>
                      <td className="sticky left-0 z-10 border-r border-gray-200 bg-inherit px-4 py-3">
                        <Link
                          to={`/operators/${operator.id}`}
                          className="font-medium text-gray-900 hover:text-guild-green"
                        >
                          {operator.firstName} {operator.lastName}
                        </Link>
                        <div className="text-xs text-gray-500">{operator.employeeId}</div>
                      </td>
                      {matrixData.sops.map((sop) => {
                        const cell = cellMap.get(`${operator.id}:${sop.id}`);
                        const avgScore = cell?.averageScore ?? null;

                        return (
                          <td key={sop.id} className="px-2 py-2 text-center">
                            <button
                              onClick={() => handleCellClick(operator.id, sop.id)}
                              className={`
                                inline-flex min-w-[60px] flex-col items-center justify-center gap-0.5
                                rounded-lg px-3 py-2
                                text-sm font-semibold
                                transition-all duration-200
                                ${getScoreColorWithHover(avgScore)}
                                ${avgScore !== null ? 'cursor-pointer' : 'cursor-default'}
                              `}
                              disabled={avgScore === null}
                            >
                              <span>{avgScore !== null ? avgScore.toFixed(0) : '—'}</span>
                              {cell && <TrendIcon trend={cell.trend} />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Drill-down Modal */}
      {selectedCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Assessment History</h2>
              <button
                onClick={closeDrillDown}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {isCellLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : cellData ? (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="rounded-xl bg-gray-50 p-4">
                    <p className="font-semibold text-gray-900">
                      {cellData.operator.firstName} {cellData.operator.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{cellData.sop.title}</p>
                  </div>

                  {/* Stats */}
                  {cellData.stats && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-xl bg-guild-mint/20 p-3">
                        <p className="text-2xl font-bold text-guild-green">
                          {cellData.stats.averageScore.toFixed(0)}
                        </p>
                        <p className="text-xs text-gray-500">Avg Score</p>
                      </div>
                      <div className="rounded-xl bg-gray-50 p-3">
                        <p className="text-2xl font-bold text-gray-900">
                          {cellData.stats.assessmentCount}
                        </p>
                        <p className="text-xs text-gray-500">Assessments</p>
                      </div>
                    </div>
                  )}

                  {/* History */}
                  <div className="max-h-64 space-y-2 overflow-y-auto">
                    {cellData.history.length === 0 ? (
                      <p className="py-4 text-center text-sm text-gray-500">
                        No assessments yet for this combination.
                      </p>
                    ) : (
                      cellData.history.map((assessment) => (
                        <Link
                          key={assessment.id}
                          to={`/assessments/${assessment.id}`}
                          className="flex items-center justify-between rounded-lg border border-gray-100 p-3 transition-colors hover:bg-gray-50"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                assessment.assessmentType === 'auto'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-purple-50 text-purple-700'
                              }`}>
                                {assessment.assessmentType === 'auto' ? 'Auto' : 'Manual'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(assessment.assessedAt)}
                              </span>
                            </div>
                          </div>
                          <span className={`rounded-lg px-2.5 py-1 text-sm font-bold ${getScoreColor(assessment.overallScore, 'badge')}`}>
                            {assessment.overallScore.toFixed(0)}
                          </span>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-gray-500">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Group Cell Drill-Down Modal */}
      {selectedGroupCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                {selectedGroupCell.groupBy === 'shift' && <Clock size={18} className="text-blue-600 shrink-0" />}
                {selectedGroupCell.groupBy === 'position' && <Briefcase size={18} className="text-purple-600 shrink-0" />}
                {selectedGroupCell.groupBy === 'station' && <Locate size={18} className="text-amber-600 shrink-0" />}
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 truncate">
                    {groupCellData?.groupName ?? 'Loading...'}
                  </h2>
                  {groupCellData?.sop && (
                    <p className="text-sm text-gray-500 truncate">{groupCellData.sop.title}</p>
                  )}
                </div>
              </div>
              <button
                onClick={closeGroupDrillDown}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto">
              {isGroupCellLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : groupCellData ? (
                <div className="space-y-4">
                  {/* Stats bar */}
                  {groupCellData.stats && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-xl bg-guild-mint/20 p-3">
                        <p className="text-2xl font-bold text-guild-green">
                          {groupCellData.stats.averageScore !== null
                            ? groupCellData.stats.averageScore.toFixed(0)
                            : '—'}
                        </p>
                        <p className="text-xs text-gray-500">Avg Score</p>
                      </div>
                      <div className="rounded-xl bg-gray-50 p-3">
                        <p className="text-2xl font-bold text-gray-900">
                          {groupCellData.stats.operatorsAssessed}
                          <span className="text-sm font-normal text-gray-400">
                            /{groupCellData.stats.totalOperators}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">Assessed</p>
                      </div>
                      <div className="rounded-xl bg-gray-50 p-3">
                        <div className="flex items-baseline gap-1">
                          {groupCellData.stats.highestScore !== null ? (
                            <>
                              <span className={`text-sm font-bold ${getScoreColor(groupCellData.stats.highestScore, 'text')}`}>
                                {groupCellData.stats.highestScore.toFixed(0)}
                              </span>
                              <span className="text-gray-300">/</span>
                              <span className={`text-sm font-bold ${getScoreColor(groupCellData.stats.lowestScore, 'text')}`}>
                                {groupCellData.stats.lowestScore !== null
                                  ? groupCellData.stats.lowestScore.toFixed(0)
                                  : '—'}
                              </span>
                            </>
                          ) : (
                            <span className="text-2xl font-bold text-gray-300">—</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">High / Low</p>
                      </div>
                    </div>
                  )}

                  {/* Operator list */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      Operators ({groupCellData.operators.length})
                    </h3>
                    <div className="max-h-72 space-y-1.5 overflow-y-auto">
                      {groupCellData.operators.length === 0 ? (
                        <p className="py-4 text-center text-sm text-gray-500">
                          No operators in this group.
                        </p>
                      ) : (
                        [...groupCellData.operators]
                          .sort((a, b) => {
                            // Null scores go to the bottom
                            if (a.averageScore === null && b.averageScore === null) return 0;
                            if (a.averageScore === null) return 1;
                            if (b.averageScore === null) return -1;
                            // Lowest scores first (skill gaps at top)
                            return a.averageScore - b.averageScore;
                          })
                          .map((op) => {
                            const hasScore = op.averageScore !== null;
                            return (
                              <button
                                key={op.operatorId}
                                onClick={() => hasScore && handleOperatorDrillDown(op.operatorId, groupCellData.sop.id)}
                                disabled={!hasScore}
                                className={`w-full flex items-center justify-between rounded-lg border border-gray-100 p-3 transition-colors text-left ${
                                  hasScore
                                    ? 'hover:bg-gray-50 cursor-pointer'
                                    : 'cursor-default opacity-50'
                                }`}
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {op.firstName} {op.lastName}
                                  </p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>{op.employeeId}</span>
                                    {op.assessmentCount > 0 && (
                                      <>
                                        <span className="text-gray-300">|</span>
                                        <span>{op.assessmentCount} assessment{op.assessmentCount !== 1 ? 's' : ''}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <span
                                  className={`shrink-0 rounded-lg px-2.5 py-1 text-sm font-bold ${getScoreColor(
                                    op.averageScore, 'badge'
                                  )}`}
                                >
                                  {hasScore ? op.averageScore!.toFixed(0) : '—'}
                                </span>
                              </button>
                            );
                          })
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="py-4 text-center text-sm text-gray-500">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkillMatrixPage;
